class ImperiaHomeHeaderSentinel extends HTMLElement {
  observer;
  header;

  connectedCallback() {
    this.header = document.querySelector('#header-component');
    if (!this.header) return;

    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const hasScrolledPastHeroStart = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.header.classList.toggle('imperia-home-header--scrolled', hasScrolledPastHeroStart);
    });

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.header?.classList.remove('imperia-home-header--scrolled');
  }
}

if (!customElements.get('imperia-home-header-sentinel')) {
  customElements.define('imperia-home-header-sentinel', ImperiaHomeHeaderSentinel);
}

class ImperiaSelectedEyewear extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.stage = this.querySelector('[data-selected-stage]');
    this.rail = this.querySelector('[data-selected-rail]');
    this.items = [...this.querySelectorAll('[data-selected-product]')];
    this.currentLabel = this.closest('.imperia-selected-eyewear')?.querySelector('[data-current]');
    this.steps = [...this.querySelectorAll('[data-selected-step]')];
    this.previous = this.querySelector('[data-selected-previous]');
    this.next = this.querySelector('[data-selected-next]');

    if (!this.stage || !this.rail || !this.items.length || !this.currentLabel) return;

    this.initialized = true;
    this.abortController = new AbortController();
    this.current = this.normalize(Number(this.dataset.initialIndex || 0));
    this.drag = null;
    this.suppressedDragClick = null;
    const options = { signal: this.abortController.signal };

    this.previous?.addEventListener('click', () => this.select(this.current - 1), options);
    this.next?.addEventListener('click', () => this.select(this.current + 1), options);
    this.stage.addEventListener('keydown', this.handleKeydown, options);
    this.stage.addEventListener('click', this.handleStageClick, { ...options, capture: true });
    this.stage.addEventListener('pointerdown', this.handlePointerDown, options);
    this.stage.addEventListener('pointermove', this.handlePointerMove, options);
    this.stage.addEventListener('pointerup', this.handlePointerEnd, options);
    this.stage.addEventListener('pointercancel', this.handlePointerEnd, options);

    this.items.forEach((item, index) => {
      item.addEventListener('keydown', (event) => {
        if ((event.key === 'Enter' || event.key === ' ') && index !== this.current) {
          event.preventDefault();
          this.select(index, { keyboard: true });
        }
      }, options);
    });

    this.steps.forEach((step, index) => {
      step.addEventListener('click', () => this.select(index), options);
    });

    if (this.items.length === 1) this.classList.add('is-single');
    this.render();
    this.readyFrame = requestAnimationFrame(() => this.classList.add('is-ready'));
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    cancelAnimationFrame(this.readyFrame);
    this.abortController?.abort();
    this.initialized = false;
  }

  normalize(index) {
    return ((index % this.items.length) + this.items.length) % this.items.length;
  }

  select(index, { keyboard = false } = {}) {
    const next = this.normalize(index);
    if (next === this.current) return;

    this.current = next;
    this.render();
    if (keyboard) requestAnimationFrame(() => this.items[this.current].focus({ preventScroll: true }));
  }

  render() {
    const count = this.items.length;

    this.items.forEach((item, index) => {
      let offset = index - this.current;
      if (offset > count / 2) offset -= count;
      if (offset < -count / 2) offset += count;

      let position = 'hidden';
      if (offset === 0) position = 'active';
      else if (offset === -1) position = 'previous';
      else if (offset === 1) position = 'next';
      else if (offset < 0) position = 'far-previous';
      else position = 'far-next';

      const visible = Math.abs(offset) <= 1;
      item.dataset.position = position;
      item.style.setProperty('--imperia-selected-position', offset);
      item.tabIndex = visible ? 0 : -1;
      item.setAttribute('aria-hidden', String(!visible));
      item.setAttribute('aria-current', String(position === 'active'));
      item.querySelectorAll('a').forEach((link) => {
        link.tabIndex = position === 'active' ? 0 : -1;
      });
    });

    this.currentLabel.textContent = String(this.current + 1).padStart(2, '0');
    this.steps.forEach((step, index) => step.setAttribute('aria-current', String(index === this.current)));
  }

  handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.select(this.current - 1, { keyboard: true });
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.select(this.current + 1, { keyboard: true });
    }
  };

  handleStageClick = (event) => {
    if (this.suppressedDragClick) {
      const withinDragRelease = Math.abs(event.clientX - this.suppressedDragClick.x) < 12
        && Math.abs(event.clientY - this.suppressedDragClick.y) < 12;
      this.suppressedDragClick = null;
      if (withinDragRelease) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
    }

    const item = event.target.closest('[data-selected-product]');
    if (!item) return;

    const index = this.items.indexOf(item);
    if (index === -1 || index === this.current) return;

    event.preventDefault();
    this.select(index);
  };

  handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('button')) return;

    this.suppressedDragClick = null;
    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startTime: performance.now(),
      axis: null,
    };
  };

  handlePointerMove = (event) => {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const delta = event.clientX - this.drag.startX;
    const deltaY = event.clientY - this.drag.startY;

    if (!this.drag.axis && (Math.abs(delta) > 6 || Math.abs(deltaY) > 6)) {
      this.drag.axis = Math.abs(delta) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
    }

    if (this.drag.axis !== 'horizontal') return;

    const damped = Math.max(-72, Math.min(72, delta * 0.34));
    this.rail.classList.add('is-dragging');
    this.rail.style.transform = `translate3d(${damped}px, 0, 0)`;
  };

  handlePointerEnd = (event) => {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;

    const delta = event.clientX - this.drag.startX;
    const deltaY = event.clientY - this.drag.startY;
    const elapsed = Math.max(1, performance.now() - this.drag.startTime);
    const velocity = Math.abs(delta) / elapsed;
    const isHorizontal = this.drag.axis === 'horizontal' && Math.abs(delta) > Math.abs(deltaY);
    this.rail.classList.remove('is-dragging');
    this.rail.style.transform = '';
    this.drag = null;

    if (isHorizontal && (Math.abs(delta) > 44 || (Math.abs(delta) > 18 && velocity > 0.32))) {
      this.suppressedDragClick = { x: event.clientX, y: event.clientY };
      this.select(this.current + (delta < 0 ? 1 : -1));
    }
  };
}

if (!customElements.get('imperia-selected-eyewear')) {
  customElements.define('imperia-selected-eyewear', ImperiaSelectedEyewear);
}
